/* eslint-disable css-modules/no-unused-class, no-extra-boolean-cast  */
import { Button, Icon } from 'learning-panel-components';
import { FC, useCallback, useEffect, useState } from 'react';
import { translate } from 'translations';
import { useSelector } from 'react-redux';
import { coreService } from 'network-utils';
import { selectToken, selectIsTournamentsGamePackageEnabled } from '@redux/slices/userSlice';
import { useGetTournamentResult } from '@queries/tournaments/tournament';
import { useGetCoreTournamentResult } from '@queries/core/tournaments';
import TournamentLeaderboard from '../leaderboard/tournamentLeaderboard';
import OverallAchievements from '../overall/overallAchievements';
import VulnerabilityBreakDown from '../tournament/vulnerabilityBreakDown/vulnerabilityBreakDown';
import TournamentBanner from '../tournamentBanner';
import TournamentsResults from '../tournamentsResults/tournamentsResults';
import styles from './tournamentsPage.module.scss';

const TournamentResultPage: FC<{
    setIsOpenTournamentLeaderboard: Function;
    setTournamentUuid: Function;
    locale: string;
}> = (props: { setIsOpenTournamentLeaderboard: Function; setTournamentUuid: Function; locale: string }) => {
    const { setIsOpenTournamentLeaderboard, setTournamentUuid, locale } = props;
    const tournamentUuidParam = new URLSearchParams(window.location.search).get('tournamentUuid');
    const tournamentUuid = tournamentUuidParam || '';
    const [vulnerabilities, setVulnerabilities] = useState<coreService.VulnerabilityData[]>([]);
    const token = useSelector(selectToken);
    const isTournamentGamePackageEnabled = useSelector(selectIsTournamentsGamePackageEnabled);

    const [tournament, setTournament] = useState<any>();
    const [tournamentUserResult, setTournamentResult] = useState<any>();
    const [tournamentOverall, setTournamentOverall] = useState<any>();
    const { data: tournamentResult, isLoading: tournamentResultLoading } = useGetTournamentResult(
        token,
        tournamentUuid,
        isTournamentGamePackageEnabled
    );
    const { data: coreTournamentResult, isLoading: coreTournamentResultLoading } = useGetCoreTournamentResult(
        token,
        tournamentUuid,
        isTournamentGamePackageEnabled
    );

    const handleOnClickTournament = () => {
        setIsOpenTournamentLeaderboard(true);
        setTournamentUuid(tournamentUuidParam);
    };

    const buildTournamentResults = (
        rounds: coreService.QuestionResult[][],
        tournamentName: string,
        tournamentDate: string
    ) => {
        const tournamentResultsTemp: any[] = [];
        rounds.map((round: any, i: number) => {
            tournamentResultsTemp.push({
                name: tournamentName,
                totalQuestions: round.length,
                correctQuestions: round.filter((question: any) => question.correct).length,
                scheduledDate: tournamentDate,
                roundNumber: i + 1,
                totalRounds: rounds.length,
                answers: round
            });
        });

        return tournamentResultsTemp;
    };

    const setUserResults = useCallback(
        (tournamentData: coreService.TournamentData, userResults: coreService.UserResults) => {
            setTournamentOverall({
                winner: userResults.rank === 1 ? 'You' : tournamentData.winner,
                rank: userResults.rank,
                totalUsers: tournamentData.participants,
                framework: userResults.framework,
                score: userResults.score,
                strongCategories: [userResults.vulnerabilities[0].vulnerabilityName],
                poorCategories: [userResults.vulnerabilities[userResults.vulnerabilities.length - 1].vulnerabilityName]
            });
            setVulnerabilities(userResults.vulnerabilities);

            setTournament({
                name: tournamentData.name,
                dateTime: tournamentData.dateTime,
                numberOfRounds: tournamentData.numberOfRounds,
                numberOfUsers: tournamentData.participants,
                frameworks: tournamentData.frameworks
            });

            const tournamentsResults = buildTournamentResults(
                userResults.questionsBreakdown,
                tournamentData.name,
                tournamentData.dateTime
            );

            setTournamentResult(tournamentsResults);
        },
        []
    );

    useEffect(() => {
        if (tournamentResult && tournamentResult.success && !isTournamentGamePackageEnabled) {
            const data = tournamentResult.userResults;
            const { tournamentData, userResults } = data;

            setUserResults(tournamentData, userResults);
        } else if (coreTournamentResult && coreTournamentResult.success && isTournamentGamePackageEnabled) {
            const { userResults, tournamentData } = coreTournamentResult;

            setUserResults(tournamentData, userResults);
        }
    }, [
        tournamentResult,
        tournamentResultLoading,
        coreTournamentResult,
        coreTournamentResultLoading,
        isTournamentGamePackageEnabled,
        setUserResults
    ]);

    return (
        <>
            <div className={styles['content-wrapper']}>
                <div className={styles.title}>
                    <div className={styles.text}>{translate('TournamentsResults')}</div>
                </div>
                {tournament && (
                    <div className={styles.banner}>
                        <TournamentBanner
                            name={tournament.name}
                            frameworks={tournament.frameworks}
                            status={'finished'}
                            numberOfRounds={tournament.numberOfRounds}
                            numberOfUsers={tournament.numberOfUsers}
                            dateTime={tournament.dateTime}
                            numJoinedUsers={0}
                            participants={[]}
                            canJoin={false}
                            canManage={false}
                            locale={locale}
                        />
                    </div>
                )}

                <div className={styles.title}>
                    <div className={styles.text}>{translate('OverallAchievements')}</div>
                </div>
                {tournamentOverall && (
                    <OverallAchievements
                        framework={tournamentOverall.framework}
                        score={tournamentOverall.score}
                        winnerText={tournamentOverall.winner}
                        strongCategories={tournamentOverall.strongCategories}
                        poorCategories={tournamentOverall.poorCategories}
                        rank={tournamentOverall.rank}
                        totalUsers={tournamentOverall.totalUsers}
                    />
                )}
                <div className={styles['title-button']}>
                    <div className={styles.title}>
                        <div className={styles.text}>{translate('Top10Tournaments')}</div>
                    </div>

                    <div style={{ height: '3rem' }}>
                        <Button
                            type="learningPanel"
                            text={translate('openFullLeaderboard')}
                            endIcon={<Icon name="popup" style={{ marginTop: '0.5rem' }} />}
                            onClick={() => handleOnClickTournament()}
                        />
                    </div>
                </div>
                {tournamentUuidParam && (
                    <div className={styles.leaderboard}>
                        <TournamentLeaderboard tournamentUuid={tournamentUuidParam} />
                    </div>
                )}
                <div className={styles.title}>
                    <div className={styles.text}>{translate('PersonalVulnerability')}</div>
                </div>
                {vulnerabilities.length > 0 && <VulnerabilityBreakDown vulnerabilities={vulnerabilities} />}
                <div className={styles.title}>
                    <div className={styles.text}>{translate('QuestionBreakdown')}</div>
                </div>
                {tournamentUserResult && <TournamentsResults tournamentsResults={tournamentUserResult} />}
            </div>
        </>
    );
};

export default TournamentResultPage;
